"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Lenis from "@studio-freight/lenis";

/**
 * @file lenis-provider.tsx
 * @description Lenis 스무스 스크롤 Provider 컴포넌트
 *
 * 주요 기능:
 * 1. Lenis 인스턴스 초기화 및 관리
 * 2. requestAnimationFrame을 통한 부드러운 스크롤 애니메이션
 * 3. 컴포넌트 언마운트 시 정리(cleanup)
 * 4. 대시보드 페이지에서는 Lenis 비활성화 (중첩 스크롤 충돌 방지)
 *
 * 핵심 구현 로직:
 * - 클라이언트 사이드에서만 Lenis 인스턴스를 초기화 (SSR 호환)
 * - requestAnimationFrame을 사용하여 매 프레임마다 Lenis 업데이트
 * - 기본 설정으로 부드러운 스크롤 경험 제공
 * - 대시보드 경로에서는 Lenis를 비활성화하여 중첩 스크롤 충돌 방지
 *
 * @dependencies
 * - @studio-freight/lenis: 스무스 스크롤 라이브러리
 * - next/navigation: usePathname 훅
 */

export function LenisProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isDashboard = pathname?.startsWith('/dashboard');
  const isAdmin = pathname?.startsWith('/admin');

  useEffect(() => {
    // 대시보드 및 관리자 페이지에서는 Lenis 비활성화 (중첩 스크롤 충돌 방지)
    if (isDashboard || isAdmin) {
      return;
    }

    // Lenis 인스턴스 생성 (기본 설정)
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
    });

    // requestAnimationFrame을 사용하여 Lenis 업데이트
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // 컴포넌트 언마운트 시 정리
    return () => {
      lenis.destroy();
    };
  }, [isDashboard, isAdmin]);

  return <>{children}</>;
}

